// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useMemo } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { qrCodeTreats } from './qrCodeTreats';

type QrCodeVariation = PsfPromoteOrSharePageData['psfPromoteOrShareQrCodeExperiment']['variation'];

/** What a treatment arm adds to the share slide-up. `null` (control, `fake_control`, ineligible) renders production. */
export type QrCodeLayout = Readonly<{
	/** Appends the "QR code" tile after the existing channel tiles. */
	showQrTile: true;
	/** `variant_2` only: the QR view offers Print poster next to Save image. */
	showPrint: boolean;
}>;

// Keyed on the config's own variations union, so a renamed arm fails to compile instead of silently
// rendering production. `control` and `fake_control` are absent on purpose: both render production.
const ARM_LAYOUTS: Readonly<Partial<Record<QrCodeVariation, QrCodeLayout>>> = {
	variant_1: { showQrTile: true, showPrint: false },
	variant_2: { showQrTile: true, showPrint: true },
};

/**
 * psf_promote_or_share_qr_code_experiment (CHANGE-99266): treats and fires `$exposure` when the share
 * slide-up opens, in every arm, because the treatment changes the grid every opener sees. So the
 * treated population equals the exposed population equals slide-up openers.
 *
 * No explicit mobile-only check: the slide-up's trigger is itself mobile-gated (`md:hidden` in
 * `ShareSlideUp`), so enrollment is already restricted to that surface, as for the sibling
 * slide-up experiments.
 */
export function useTreatQrCodeExperiment(): {
	treatOnSlideUpOpen: () => Promise<void>;
	qrCodeLayout: QrCodeLayout | null;
} {
	const {
		data: { psfPromoteOrShareQrCodeExperiment: experiment },
	} = usePsfPromoteOrSharePageData();
	const utilityContext = useUtilityContext();

	// `treatExperiment` bypasses the config's `enabledIf`, so the flag is read again here. `!fallback`
	// keeps treated == exposed: `useTrackExperimentExposure` refuses to expose a fallback resolution, so
	// treating one would enroll a session that can never expose. It already covers the sentinel name,
	// which is kept as belt and braces.
	const isEligible =
		experiment.config.enabled && experiment.experimentName !== '__invalid_experiment__' && !experiment.fallback;

	// An override forces the arm the page shows, but `treatExperiment` would register the session's real
	// assignment, and `useTrackExperimentExposure` never exposes an override. So an override session sees
	// its forced arm (`qrCodeLayout` still reads `isEligible`) and is never treated.
	const treatsOnOpen = isEligible && !experiment.override;

	// `treatExperiment` registers the treatment server-side, but the resolved object stays
	// `treated: false`, and `useTrackExperimentExposure` does nothing while `!treated`. So the exposure
	// object is armed with `treated: true` here. Safe only because the one call site below fires it on
	// `treatExperiment`'s own `true` return. Do not call `trackExposure` from anywhere else.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	const treatOnSlideUpOpen = useCallback(async () => {
		if (!treatsOnOpen) return undefined;
		const { experimentName } = experiment;

		let treat = qrCodeTreats.get(experimentName);
		if (!treat) {
			treat = (async () => {
				// `treatExperiment` resolves `false`, rather than throwing, when the treatment did not land
				// server-side (network failure, bot session), so `$exposure` is gated on that return. A throw
				// is treated the same way. A rejected exposure send is reported, not rethrown: the enrollment
				// stands, and the slide-up open must not surface an analytics failure.
				try {
					if (await treatExperiment(utilityContext, experimentName)) {
						await trackExposure();
					}
				} catch (error) {
					// The latch stays set, so a reopen does not treat again.
					void utilityContext.errorReporter.report(
						{
							error: new Error('QR code experiment treat or $exposure failed', { cause: error }),
							context: { feature: 'psf_promote_or_share_qr_code_experiment' },
						},
						{ severity: 'warning' },
					);
				}
			})();
			qrCodeTreats.set(experimentName, treat);
		}
		return treat;
	}, [treatsOnOpen, experiment, utilityContext, trackExposure]);

	// Derived from the resolved assignment rather than from the treat, so the treatment grid renders on
	// the slide-up's first frame. It shares the treat's eligibility gate, so a treatment layout only
	// renders for a signer the open would enroll (or a QA override).
	const qrCodeLayout = (isEligible && ARM_LAYOUTS[experiment.variation]) || null;

	return { treatOnSlideUpOpen, qrCodeLayout };
}
